// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Profile Page
 *
 * Manages artist profile, links, and social accounts.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  ArrowLeft,
  User,
  Link as LinkIcon,
  Share2,
  Loader2,
  Save,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  GripVertical,
  ExternalLink,
  EyeOff,
  Store,
  Instagram,
  Twitter,
  Facebook,
  Youtube,
  Linkedin,
  Globe,
  Images,
} from 'lucide-react'
import {
  LINK_CATEGORIES,
  LINK_CATEGORY_LABELS,
  classifyLinkCategory,
  type LinkCategory,
} from '../lib/linkCategory'
import {
  useProfile,
  useUpdateProfile,
  useProfileLinks,
  useCreateLink,
  useUpdateLink,
  useDeleteLink,
  useSocialAccounts,
  useUpdateSocial,
  usePreviewUrl,
} from '../hooks'
import type { ProfileLink, SocialAccounts } from '../types'
import { FeaturedWorksDialog, type FeaturedWorksSubmit } from '../components/FeaturedWorksDialog'

// =============================================================================
// Form Schemas
// =============================================================================

const profileSchema = z.object({
  handle: z
    .string()
    .min(3, 'Handle must be at least 3 characters')
    .max(50)
    .regex(/^[a-z0-9-]+$/, 'Handle can only contain lowercase letters, numbers, and hyphens'),
  name: z.string().min(1, 'Name is required').max(255),
  bio: z.string().max(500).optional(),
  location: z.string().max(255).optional(),
})

const linkSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255),
  url: z
    .string()
    .url('Must be a valid URL')
    .refine((u) => /^https?:\/\//i.test(u), 'URL must start with http:// or https://'),
  // 'auto' == auto-detect (let the server classifier decide); otherwise an explicit category.
  // A non-empty sentinel is required because Radix <SelectItem> disallows an empty-string value.
  category: z.enum(['auto', ...LINK_CATEGORIES] as [string, ...string[]]).optional(),
  hidden: z.boolean().optional(),
})

const socialSchema = z.object({
  instagram: z.string().max(255).optional(),
  twitter: z.string().max(255).optional(),
  facebook: z.string().max(255).optional(),
  youtube: z.string().max(255).optional(),
  linkedin: z.string().max(255).optional(),
  website: z.string().url().optional().or(z.literal('')),
})

type ProfileFormData = z.infer<typeof profileSchema>
type LinkFormData = z.infer<typeof linkSchema>
type SocialFormData = z.infer<typeof socialSchema>

// =============================================================================
// Link Form Dialog
// =============================================================================

interface LinkFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  link?: ProfileLink | null
  onSubmit: (data: LinkFormData) => Promise<void>
  isSubmitting: boolean
}

/** Read the form-shaped values out of a stored ProfileLink (which nests under `data`). */
function linkToFormValues(link?: ProfileLink | null): LinkFormData {
  return {
    title: link?.data?.title || '',
    url: link?.data?.link?.url || '',
    category: (link?.data?.category as LinkCategory | undefined) || 'auto',
    hidden: link?.data?.hidden ?? false,
  }
}

function LinkFormDialog({
  open,
  onOpenChange,
  link,
  onSubmit,
  isSubmitting,
}: LinkFormDialogProps) {
  const isEditing = !!link

  const form = useForm<LinkFormData>({
    resolver: zodResolver(linkSchema),
    defaultValues: linkToFormValues(link),
  })

  useEffect(() => {
    form.reset(linkToFormValues(link))
  }, [link, form])

  // Live classifier suggestion for the current URL — mirrors what the public API resolves when the
  // category is left on "Auto-detect", so the artist can see where a link will land before saving.
  const urlValue = form.watch('url')
  const suggestion = classifyLinkCategory(urlValue || '')

  const handleSubmit = async (data: LinkFormData) => {
    await onSubmit(data)
    form.reset(linkToFormValues(null))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Link' : 'Add Link'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the link details' : 'Add a new link to your profile'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="My Shop" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://example.com" type="url" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select value={field.value || 'auto'} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Auto-detect" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="auto">
                        Auto-detect{suggestion ? `: ${LINK_CATEGORY_LABELS[suggestion]}` : ''}
                      </SelectItem>
                      {LINK_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {LINK_CATEGORY_LABELS[c]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Used to group links on your storefront. Leave on Auto-detect to classify by URL.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hidden"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Hidden</FormLabel>
                    <FormDescription>
                      Keep this link but exclude it from your public profile and storefront.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save' : 'Add Link'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Links List Component
// =============================================================================

interface LinksListProps {
  links: ProfileLink[]
  onEdit: (link: ProfileLink) => void
  onDelete: (id: number) => void
}

function LinksList({ links, onEdit, onDelete }: LinksListProps) {
  if (links.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No links added yet. Add links to share with your visitors.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {links.map((link) => {
        const url = link.data?.link?.url || ''
        const category = link.data?.category as LinkCategory | undefined
        const detected = !category && url ? classifyLinkCategory(url) : null
        const isHidden = link.data?.hidden === true
        return (
        <div
          key={link.id}
          className={`flex items-center gap-3 p-3 border rounded-lg bg-muted/50 ${
            isHidden ? 'opacity-60' : ''
          }`}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium">{link.data?.title}</p>
              {(category || detected) && (
                <Badge variant={category ? 'secondary' : 'outline'} className="capitalize">
                  {category || detected}
                  {!category && detected ? ' (auto)' : ''}
                </Badge>
              )}
              {isHidden && (
                <Badge variant="outline" className="gap-1">
                  <EyeOff className="h-3 w-3" />
                  Hidden
                </Badge>
              )}
            </div>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:underline flex items-center gap-1"
              >
                {url}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <p className="text-sm text-muted-foreground">
                {link.type === 'featured'
                  ? `Featured Works · ${link.data?.artworks?.length || 0} pieces`
                  : <span className="capitalize">{link.type}</span>}
              </p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(link)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(link.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        )
      })}
    </div>
  )
}

// =============================================================================
// Social Icon Component
// =============================================================================

function SocialIcon({ platform }: { platform: string }) {
  switch (platform) {
    case 'instagram':
      return <Instagram className="h-5 w-5" />
    case 'twitter':
      return <Twitter className="h-5 w-5" />
    case 'facebook':
      return <Facebook className="h-5 w-5" />
    case 'youtube':
      return <Youtube className="h-5 w-5" />
    case 'linkedin':
      return <Linkedin className="h-5 w-5" />
    case 'website':
      return <Globe className="h-5 w-5" />
    default:
      return <LinkIcon className="h-5 w-5" />
  }
}

// =============================================================================
// Main Page Component
// =============================================================================

export function ProfilePage() {
  const navigate = useNavigate()

  // Queries
  const { data: profile, isLoading: isLoadingProfile } = useProfile()
  const { data: links, isLoading: isLoadingLinks } = useProfileLinks()
  const { data: social, isLoading: isLoadingSocial } = useSocialAccounts()
  // The API owns which host the link-in-bio app is served on (PROFILE_BASE_URL);
  // the handle field below used to hardcode a different one, and got it wrong.
  const { data: previewData } = usePreviewUrl()

  // Mutations
  const updateProfile = useUpdateProfile()
  const createLink = useCreateLink()
  const updateLink = useUpdateLink()
  const deleteLink = useDeleteLink()
  const updateSocial = useUpdateSocial()

  // State
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<ProfileLink | null>(null)
  const [deleteLinkId, setDeleteLinkId] = useState<number | null>(null)
  const [featuredDialogOpen, setFeaturedDialogOpen] = useState(false)
  const [editingFeatured, setEditingFeatured] = useState<ProfileLink | null>(null)

  // Profile Form
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      handle: '',
      name: '',
      bio: '',
      location: '',
    },
  })

  // Social Form
  const socialForm = useForm<SocialFormData>({
    resolver: zodResolver(socialSchema),
    defaultValues: {
      instagram: '',
      twitter: '',
      facebook: '',
      youtube: '',
      linkedin: '',
      website: '',
    },
  })

  // Update forms when data loads
  useEffect(() => {
    if (profile) {
      profileForm.reset({
        handle: profile.handle || '',
        name: profile.name || '',
        bio: profile.bio || '',
        location: profile.location || '',
      })
    }
  }, [profile, profileForm])

  useEffect(() => {
    if (social) {
      socialForm.reset({
        instagram: social.instagram || '',
        twitter: social.twitter || '',
        facebook: social.facebook || '',
        youtube: social.youtube || '',
        linkedin: social.linkedin || '',
        website: social.website || '',
      })
    }
  }, [social, socialForm])

  const handleProfileSubmit = async (data: ProfileFormData) => {
    await updateProfile.mutateAsync(data)
  }

  const handleSocialSubmit = async (data: SocialFormData) => {
    await updateSocial.mutateAsync(data)
  }

  const handleLinkSubmit = async (data: LinkFormData) => {
    // Map the flat form to the stored link shape ({ type, data: { … } }). An empty category means
    // "auto-detect": persist null so the server-side classifier resolves it at read time.
    const payload = {
      type: (editingLink?.type as ProfileLink['type']) || ('link' as const),
      data: {
        title: data.title,
        link: { url: data.url, target: '_blank' as const },
        category: data.category && data.category !== 'auto' ? data.category : null,
        hidden: !!data.hidden,
      },
    }
    if (editingLink) {
      await updateLink.mutateAsync({ id: editingLink.id, data: payload })
    } else {
      await createLink.mutateAsync(payload)
    }
    setLinkDialogOpen(false)
    setEditingLink(null)
  }

  // The Featured Works card is not a URL link, so it gets its own editor (artwork picker).
  const openLinkEditor = (link: ProfileLink | null) => {
    if (link?.type === 'featured') {
      setEditingFeatured(link)
      setFeaturedDialogOpen(true)
      return
    }
    setEditingLink(link)
    setLinkDialogOpen(true)
  }

  const handleFeaturedSubmit = async (data: FeaturedWorksSubmit) => {
    const payload = { type: 'featured' as const, data }
    if (editingFeatured) {
      await updateLink.mutateAsync({ id: editingFeatured.id, data: payload })
    } else {
      await createLink.mutateAsync(payload)
    }
    setFeaturedDialogOpen(false)
    setEditingFeatured(null)
  }

  const featuredCard = (links || []).find((l) => l.type === 'featured') || null
  // Each newly featured work links to the artist's shop by default, when they have one listed.
  const shopUrl =
    (links || []).find((l) => l.type === 'link' && l.data?.category === 'shop' && l.data?.link?.url)?.data
      ?.link?.url ||
    (links || []).find((l) => l.type === 'link' && l.data?.link?.url && classifyLinkCategory(l.data.link.url) === 'shop')
      ?.data?.link?.url

  const handleDeleteLink = async () => {
    if (deleteLinkId) {
      await deleteLink.mutateAsync(deleteLinkId)
      setDeleteLinkId(null)
    }
  }

  const isLoading = isLoadingProfile || isLoadingLinks || isLoadingSocial

  if (isLoading) {
    return (
      <div className="space-y-6 p-8">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Profile</h1>
          <p className="text-muted-foreground">
            Manage your artist profile and links
          </p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="links">
            <LinkIcon className="h-4 w-4 mr-2" />
            Links
          </TabsTrigger>
          <TabsTrigger value="social">
            <Share2 className="h-4 w-4 mr-2" />
            Social
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Your public profile information shown on your portfolio
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...profileForm}>
                <form
                  onSubmit={profileForm.handleSubmit(handleProfileSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={profileForm.control}
                    name="handle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Handle</FormLabel>
                        <FormControl>
                          <div className="flex items-center">
                            <span className="text-muted-foreground mr-2">@</span>
                            <Input
                              {...field}
                              placeholder="your-handle"
                              className="lowercase"
                              onChange={(e) =>
                                field.onChange(e.target.value.toLowerCase())
                              }
                            />
                          </div>
                        </FormControl>
                        <FormDescription>
                          Your unique portfolio URL:{' '}
                          {previewData?.publicUrl
                            ? previewData.publicUrl.replace(/^https?:\/\//, '')
                            : `arthaus.bio/${field.value || 'your-handle'}`}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Display Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Your Artist Name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bio</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Tell visitors about yourself..."
                            rows={4}
                          />
                        </FormControl>
                        <FormDescription>
                          A short bio shown on your portfolio
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="City, Country" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={updateProfile.isPending}>
                      {updateProfile.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <Save className="mr-2 h-4 w-4" />
                      Save Profile
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Links Tab */}
        <TabsContent value="links">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Profile Links</CardTitle>
                <CardDescription>
                  Add links to share with your portfolio visitors
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => openLinkEditor(featuredCard)}>
                  <Images className="h-4 w-4 mr-2" />
                  {featuredCard ? 'Edit Featured Works' : 'Add Featured Works'}
                </Button>
                <Button onClick={() => openLinkEditor(null)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Link
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <Store className="h-4 w-4" />
                <AlertDescription>
                  These links now also feed your storefront <span className="font-medium">/shop</span> page,
                  grouped by category. Editing or hiding a link here changes what visitors see there.
                </AlertDescription>
              </Alert>
              <LinksList
                links={links || []}
                onEdit={openLinkEditor}
                onDelete={setDeleteLinkId}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Tab */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle>Social Accounts</CardTitle>
              <CardDescription>
                Connect your social media profiles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...socialForm}>
                <form
                  onSubmit={socialForm.handleSubmit(handleSocialSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={socialForm.control}
                    name="instagram"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <SocialIcon platform="instagram" />
                          Instagram
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="username" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={socialForm.control}
                    name="twitter"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <SocialIcon platform="twitter" />
                          Twitter / X
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="username" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={socialForm.control}
                    name="facebook"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <SocialIcon platform="facebook" />
                          Facebook
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="page-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={socialForm.control}
                    name="youtube"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <SocialIcon platform="youtube" />
                          YouTube
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="channel-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={socialForm.control}
                    name="linkedin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <SocialIcon platform="linkedin" />
                          LinkedIn
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="profile-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={socialForm.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <SocialIcon platform="website" />
                          Website
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://yourwebsite.com" type="url" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={updateSocial.isPending}>
                      {updateSocial.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <Save className="mr-2 h-4 w-4" />
                      Save Social Links
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Link Form Dialog */}
      <LinkFormDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        link={editingLink}
        onSubmit={handleLinkSubmit}
        isSubmitting={createLink.isPending || updateLink.isPending}
      />

      <FeaturedWorksDialog
        open={featuredDialogOpen}
        onOpenChange={setFeaturedDialogOpen}
        link={editingFeatured}
        defaultLinkUrl={shopUrl}
        onSubmit={handleFeaturedSubmit}
        isSubmitting={createLink.isPending || updateLink.isPending}
      />

      {/* Delete Link Confirmation */}
      <AlertDialog open={!!deleteLinkId} onOpenChange={() => setDeleteLinkId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this link? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLink}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
